import React, { useRef, useMemo } from 'react';
import styles from './OverlayLayer.module.css';

// An SVG layer sitting on top of the DicomCanvas. Its viewBox is the IMAGE
// pixel space, and its CSS transform mimics the canvas's transform
// (translate + rotate + scale), so drawings in image coords line up with
// the underlying pixels.
//
// Props:
//   imageWidth, imageHeight
//   canvasWidth, canvasHeight   — the destination canvas CSS size
//   panX, panY, zoom, rotation  — same values passed to DicomCanvas
//   onPointerDown/Move/Up(imagePt, evt)
//   children  — SVG elements positioned in image coords
export default function OverlayLayer({
  imageWidth, imageHeight,
  canvasWidth, canvasHeight,
  panX = 0, panY = 0, zoom = 1, rotation = 0,
  onPointerDown, onPointerMove, onPointerUp,
  interactive = false,
  children,
}) {
  const ref = useRef(null);

  // Fitted scale — same calc as paintTransformed in dicom-renderer.
  const fit = useMemo(() => {
    if (!imageWidth || !imageHeight || !canvasWidth || !canvasHeight) return 1;
    return Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight);
  }, [imageWidth, imageHeight, canvasWidth, canvasHeight]);

  const drawW = imageWidth * fit;
  const drawH = imageHeight * fit;
  const total = fit * zoom;

  // Apply the canvas transform to the SVG via CSS so coordinates inside
  // the viewBox are in image pixels.
  const style = {
    transform: `translate(calc(${canvasWidth / 2 + panX}px - ${drawW * zoom / 2}px), calc(${canvasHeight / 2 + panY}px - ${drawH * zoom / 2}px)) rotate(${rotation}deg)`,
    transformOrigin: `${drawW * zoom / 2}px ${drawH * zoom / 2}px`,
    width:  drawW * zoom,
    height: drawH * zoom,
  };

  function toImage(clientX, clientY) {
    // Convert screen clientX/Y to image coords by walking backward through
    // the transform: screen -> wrapper local -> inverse rotate -> divide by total.
    const rect = ref.current.getBoundingClientRect();
    // Current bounding box after CSS transform: the rect.left/top is where
    // the post-transform SVG sits. We can compute SVG-local by subtracting.
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    const rad = (-rotation * Math.PI) / 180;
    const rx =  Math.cos(rad) * dx - Math.sin(rad) * dy;
    const ry =  Math.sin(rad) * dx + Math.cos(rad) * dy;
    const ix = rx / total + imageWidth / 2;
    const iy = ry / total + imageHeight / 2;
    return [ix, iy];
  }

  function handler(cb) {
    if (!cb) return undefined;
    return (evt) => {
      const pt = toImage(evt.clientX, evt.clientY);
      cb(pt, evt);
    };
  }

  return (
    <svg
      ref={ref}
      className={`${styles.overlay} ${interactive ? styles.interactive : ''}`}
      viewBox={`0 0 ${imageWidth} ${imageHeight}`}
      preserveAspectRatio="none"
      style={style}
      onPointerDown={handler(onPointerDown)}
      onPointerMove={handler(onPointerMove)}
      onPointerUp={handler(onPointerUp)}
    >
      {children}
    </svg>
  );
}
